// @file Composable to encapsulate content picker sources logic

import { isAppCapableOf } from '@@/bits/app_can'
import { supportsRecording, supportsScreenCapture, supportsVideoRecording } from '@@/bits/av_recorder'
import type { ContentPickerSourceMenuListItem } from '@@/bits/content_picker'
import { ContentPickerSource } from '@@/bits/content_picker'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { SearchFilters } from '@@/bits/web_search'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfaceScreenRecorderStore } from '@@/pinia/surface_screen_recorder_store'
import { storeToRefs } from 'pinia'
import type { ComputedRef } from 'vue'
import { computed } from 'vue'

export const usePostContentPickerSources = (): {
  isAudioSupported: boolean
  isCameraSupported: boolean
  isScreenshareSupported: boolean
  isVideoSupported: boolean
  contentSources: ComputedRef<ContentPickerSourceMenuListItem[][]>
} => {
  const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const surfaceStore = useSurfaceStore()
  const surfaceScreenRecorderStore = useSurfaceScreenRecorderStore()
  const { isScreenRecording } = storeToRefs(surfaceScreenRecorderStore)

  const isCameraSupported = true
  const isAudioSupported = supportsRecording
  const isVideoSupported = supportsVideoRecording || device.mobile
  const isScreenshareSupported = supportsScreenCapture

  const isWhiteboardToolbarMenuEnabledForMobileApp = computed(() => {
    return (
      isAppUsing('sandboxToolbarV11') &&
      surfaceStore.isWhiteboard &&
      device.app &&
      isAppCapableOf('nativeWhiteboardContentPicker')
    )
  })

  const contentSources = computed(() => {
    const section1: ContentPickerSourceMenuListItem[] = [
      {
        type: ContentPickerSource.PadletBrowser,
        text: __('Padlet'),
        ariaLabel: __('Attach another padlet'),
        icon: 'padlet_outline',
        color: 'purple',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.PadletBrowser),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.PadletBrowser,
        testId: 'attachAnotherPadletButton',
      },
      {
        type: ContentPickerSource.FileBrowser,
        text: isWhiteboardToolbarMenuEnabledForMobileApp.value ? __('Documents and Files') : __('Upload'),
        ariaLabel: __('Upload a file'),
        icon: 'upload_outline',
        color: 'purple',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.FileBrowser),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.FileBrowser,
        hiddenInputId: isWhiteboardToolbarMenuEnabledForMobileApp.value ? undefined : 'content-hidden-file-input',
        testId: 'contentPickerSourceUploadButton',
      },
      {
        type: ContentPickerSource.LinkInput,
        text: __('Link'),
        ariaLabel: __('Attach a link'),
        icon: 'link_outline',
        color: 'purple',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.LinkInput),
        disabled: false,
        actionType: 'trigger-link-input',
        testId: 'contentPickerSourceLinkButton',
      },
    ]

    let section2: ContentPickerSourceMenuListItem[] = [
      {
        type: ContentPickerSource.MediaLibrary,
        text: __('Photo and Video Library'),
        ariaLabel: __('Upload from photo library'),
        icon: 'photo_video_outline',
        activeIcon: 'photo_video',
        color: 'blue',
        visible:
          isWhiteboardToolbarMenuEnabledForMobileApp.value &&
          surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.MediaLibrary),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.MediaLibrary,
        testId: 'contentPickerSourceMediaLibraryButton',
      },
      {
        type: ContentPickerSource.PhotoBooth,
        text: __('Camera'),
        ariaLabel: __('Take a photo'),
        icon: 'camera_outline',
        color: 'blue',
        visible:
          isCameraSupported && surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.PhotoBooth),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.PhotoBooth,
        hiddenInputId: isWhiteboardToolbarMenuEnabledForMobileApp.value ? undefined : 'content-hidden-image-input',
        testId: 'contentPickerSourceCameraButton',
      },
      {
        type: ContentPickerSource.VideoRecorder,
        text: __('Video recorder'),
        ariaLabel: __('Record a video'),
        icon: 'video_outline',
        color: 'blue',
        visible:
          isVideoSupported && surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.VideoRecorder),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.VideoRecorder,
        testId: 'contentPickerSourceVideoButton',
      },
      {
        type: ContentPickerSource.AudioRecorder,
        text: __('Audio recorder'),
        ariaLabel: __('Record an audio'),
        icon: 'microphone_outline',
        color: 'blue',
        visible:
          isAudioSupported && surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.AudioRecorder),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.AudioRecorder,
        testId: 'contentPickerSourceAudioButton',
      },
      {
        type: ContentPickerSource.ScreenshareRecorder,
        text: __('Screen recorder'),
        ariaLabel: __('Record your screen'),
        icon: 'screen_recorder_outline',
        color: 'blue',
        visible:
          !isScreenRecording.value &&
          !device.tablet &&
          !device.phone &&
          isScreenshareSupported &&
          surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.ScreenshareRecorder),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.ScreenshareRecorder,
        testId: 'contentPickerSourceScreenRecordButton',
      },
      {
        type: ContentPickerSource.DrawingCanvas,
        text: __('Draw'),
        ariaLabel: __('Draw something'),
        icon: 'draw_outline',
        color: 'blue',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.DrawingCanvas),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.DrawingCanvas,
        testId: 'contentPickerSourceDrawButton',
      },
    ]
    if (surfaceStore.isWhiteboard) {
      // Remove DrawingCanvas from section2 if the whiteboard is enabled
      section2 = section2.filter((item) => item.type !== ContentPickerSource.DrawingCanvas)
    }
    const section3: ContentPickerSourceMenuListItem[] = [
      {
        type: ContentPickerSource.Poll,
        text: __('Poll'),
        ariaLabel: __('Make a poll'),
        icon: 'poll_outline',
        color: 'green',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.Poll),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.Poll,
        testId: 'makeAPollButton',
      },
      {
        type: ContentPickerSource.GoogleDrive,
        text: __('Google Drive'),
        ariaLabel: __('Upload from Google Drive'),
        icon: 'google_drive_outline',
        color: 'green',
        visible:
          isAppUsing('googleDrive') &&
          surfacePermissionsStore.amIRegistered &&
          surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.GoogleDrive),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.GoogleDrive,
        testId: 'contentPickerSourceGoogleDriveButton',
      },
      {
        type: ContentPickerSource.MicrosoftOneDrive,
        text: __('Microsoft OneDrive'),
        ariaLabel: __('Upload from Microsoft OneDrive'),
        icon: 'microsoft_onedrive_outline',
        color: 'green',
        visible:
          isAppUsing('microsoftOneDrivePicker') &&
          surfacePermissionsStore.amIRegistered &&
          surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.MicrosoftOneDrive),
        disabled: false,
        actionType: 'select',
        isNew: true,
        actionPayload: ContentPickerSource.MicrosoftOneDrive,
        testId: 'contentPickerSourceMicrosoftOneDriveButton',
      },
      {
        type: ContentPickerSource.AiImageSearch,
        text: __('AI image'),
        ariaLabel: __('Use AI Art Generator'),
        icon: 'sparkles_outline',
        color: 'green',
        isNew: true,
        visible:
          isAppUsing('aiImageSearch') &&
          surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.AiImageSearch),
        disabled: false,
        actionType: 'trigger-ai-image-search',
        testId: 'contentPickerSourceAiImageSearchButton',
      },
      {
        type: ContentPickerSource.AiTalk,
        text: __('AI speech'),
        ariaLabel: __('Use AI Speech Generator'),
        icon: 'communicate',
        color: 'green',
        isNew: true,
        visible: isAppUsing('aiTalk') && surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.AiTalk),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.AiTalk,
        testId: 'contentPickerSourceAiTalkButton',
      },
      {
        type: ContentPickerSource.PhotoAlbum,
        text: __('Photo album'),
        ariaLabel: __('Choose photos from your device'),
        icon: 'photo_video_outline',
        color: 'green',
        visible:
          isAppUsing('photoAlbum') &&
          surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.PhotoAlbum) &&
          !surfaceStore.isWhiteboard,
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.PhotoAlbum,
        testId: 'contentPickerSourcePhotoAlbumButton',
        isNew: true,
      },
    ]
    const section4: ContentPickerSourceMenuListItem[] = [
      {
        type: ContentPickerSource.ImageSearch,
        text: __('Image search'),
        ariaLabel: __('Search for an image'),
        icon: 'image_search_outline',
        color: 'orange',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.ImageSearch),
        disabled: false,
        actionType: 'focus-search-bar',
        actionPayload: SearchFilters.Images,
        testId: 'contentPickerSourceImageSearchButton',
      },
      // NOTE: For now, this composable is shared between Board and Whiteboard's content picker
      // because they are 99% similar. The only difference is this new Sticker attachment where
      // it only applies to Whiteboard. Therefore, we don't duplicate the composable to a new
      // `useWhiteboardContentPickerSources` yet but we should do it later if the differences
      // start to increase.
      {
        type: ContentPickerSource.Stickers,
        text: __('Stickers'),
        ariaLabel: __('Search for a sticker'),
        icon: 'sticker',
        color: 'orange',
        isNew: true,
        visible:
          isAppUsing('sandboxStickers') &&
          surfaceStore.isWhiteboard &&
          surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.Stickers),
        disabled: false,
        actionType: 'focus-search-bar',
        actionPayload: SearchFilters.Stickers,
        testId: 'contentPickerSourceStickerButton',
      },
      {
        type: ContentPickerSource.GifSearch,
        text: __('GIF'),
        ariaLabel: __('Search for a GIF'),
        icon: 'gif',
        color: 'orange',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.GifSearch),
        disabled: false,
        actionType: 'focus-search-bar',
        actionPayload: SearchFilters.Gif,
        testId: 'contentPickerSourceGifSearchButton',
      },
      {
        type: ContentPickerSource.VideoSearch,
        text: __('YouTube'),
        ariaLabel: __('Search on YouTube'),
        icon: 'youtube_outline',
        color: 'orange',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.VideoSearch),
        disabled: false,
        actionType: 'focus-search-bar',
        actionPayload: SearchFilters.Videos,
        testId: 'contentPickerSourceVideoSearchButton',
      },
      {
        type: ContentPickerSource.AudioSearch,
        text: __('Spotify'),
        ariaLabel: __('Search on Spotify'),
        icon: 'spotify_outline',
        color: 'orange',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.AudioSearch),
        disabled: false,
        actionType: 'focus-search-bar',
        actionPayload: SearchFilters.Audio,
        testId: 'contentPickerSourceAudioSearchButton',
      },
      {
        type: ContentPickerSource.WebBrowser,
        text: __('Web search'),
        ariaLabel: __('Search on the web'),
        icon: 'web_search_outline',
        color: 'orange',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.WebBrowser),
        disabled: false,
        actionType: 'focus-search-bar',
        actionPayload: SearchFilters.Web,
        testId: 'contentPickerSourceWebSearchButton',
      },
      {
        type: ContentPickerSource.MapBrowser,
        text: __('Location'),
        ariaLabel: __('Add a location'),
        icon: 'location_outline',
        color: 'orange',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.MapBrowser),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.MapBrowser,
        testId: 'contentPickerSourceLocationSearchButton',
      },
    ]
    return [section1, section2, section3, section4]
  })

  return { isAudioSupported, isCameraSupported, isScreenshareSupported, isVideoSupported, contentSources }
}
